<template>
    <div class="auth-container">
        <h2>Authenticating...</h2>
    </div>
</template>

<script setup>

import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { showToast } from '../../../utilities/toast-handler';
import { useAuthStore } from '../../auth/store/index';
import {
    sendSsoTelemetry,
    setSsoAttemptId,
    getSsoEmail,
} from '@/modules/sso/utils/ssoTelemetry';

const authStore = useAuthStore();
const router = useRouter();

function clearOauthQueryFromUrl() {
    // Prevent refresh / back-button from replaying the same Microsoft code/error.
    try {
        if (window.history?.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    } catch (e) {
        // ignore
    }
}

async function failToLogin(message) {
    clearOauthQueryFromUrl();
    try {
        authStore.clearLocalSession();
    } catch (e) {
        // ignore
    }
    showToast({ severity: 'warn', summary: 'Sign-in' }, message);
    router.replace({ name: 'Login' });
}

onMounted(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const state = urlParams.get('state');
    const session_state = urlParams.get('session_state');
    const oauthError = urlParams.get('error');
    const oauthErrorDescription = urlParams.get('error_description');
    const email = getSsoEmail();

    // Recover attempt id from OAuth state if present
    try {
        if (state) {
            const decoded = JSON.parse(atob(state));
            if (decoded?.sso_attempt_id) {
                setSsoAttemptId(decoded.sso_attempt_id);
            }
        }
    } catch (e) {
        // ignore malformed state
    }

    await sendSsoTelemetry('fe_callback_mounted', {
        email,
        detail: 'AuthCallback mounted after Microsoft redirect',
        has_code: !!authCode,
        has_state: !!state,
    });

    if (oauthError) {
        // Prefer Microsoft's own error_description as the primary message
        const microsoftMessage = oauthErrorDescription
            ? decodeURIComponent(String(oauthErrorDescription).replace(/\+/g, ' '))
            : oauthError;
        await sendSsoTelemetry('fe_callback_api_fail', {
            email,
            failure_reason: microsoftMessage,
            backend_status: oauthError,
            backend_message: microsoftMessage,
            detail: `Microsoft callback error=${oauthError}`,
            has_code: !!authCode,
            has_state: !!state,
        });
        await failToLogin('We could not complete Microsoft sign-in. Please try again.');
        return;
    }

    if (!authCode || !state) {
        await sendSsoTelemetry('fe_missing_auth_params', {
            email,
            failure_reason: 'AuthCallback missing code or state — cannot complete SSO',
            has_code: !!authCode,
            has_state: !!state,
        });
        await failToLogin('Sign-in was interrupted. Please try again from the login page.');
        return;
    }

    try {
        const result = await authStore.callbackSSO(authCode, state, session_state);
        clearOauthQueryFromUrl();
        if (!result?.success) {
            await sendSsoTelemetry('fe_callback_api_fail', {
                email: result?.email || email,
                failure_reason: result?.failure_reason || result?.detail || 'callbackSSO returned success=false',
                http_status: result?.http_status ?? null,
                backend_status: result?.backend_status ?? null,
                backend_message: result?.backend_message ?? null,
                user_id: result?.user_id ?? null,
                company_id: result?.company_id ?? null,
                product_count: result?.product_count ?? null,
            });
            await failToLogin('We could not finish signing you in. Please try again.');
        }
        // success: setupLoggedUser already navigates to ProductSelect — stay on Authenticating until then
    } catch (error) {
        await sendSsoTelemetry('fe_error', {
            email,
            failure_reason: 'AuthCallback threw while completing SSO',
            error_name: error?.name || 'Error',
            detail: error?.message || 'AuthCallback exception',
        });
        await failToLogin('We could not finish signing you in. Please try again.');
    }
});
</script>

<style scoped>
.auth-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    font-size: 1.5rem;
    font-weight: bold;
}
</style>
